import { ValidDate } from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Plus, X } from "lucide-react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { emptyToUndefined } from "@/utils/format.util";
import { FormField, FormFieldHint, FormSectionLabel } from "../../form-field/FormField";
import { LocationCombobox } from "../../location-picker/LocationPicker";
import { useMemberProfile } from "../MemberProfileContext";
import { OwnedField } from "./FormFields";
import { FormShell } from "./ProfileOverlayShell";
import { TitleField } from "./TitleField";
import type { FormProps } from "./types";

const personalFormSchema = z.object({
    personalHighlight: z.string(),
    personalMustKnow: z.string(),
    title: z.string(),
    firstName: z.string().trim().min(1, "First name is required"),
    lastName: z.string().trim().min(1, "Last name is required"),
    greeting: z.string(),
    dateOfBirth: ValidDate.optional(),
    occupation: z.string(),
    memberships: z.string(),
    interests: z.string(),
    loves: z.string(),
    hates: z.string(),
    topCities: z.array(
        z.object({
            city: z.string(),
            country: z.string(),
            countryCode: z.string(),
        }),
    ),
});

type PersonalFormValues = z.infer<typeof personalFormSchema>;

export function PersonalForm({ onClose }: FormProps) {
    const { profile, savePersonal } = useMemberProfile();
    const { control, handleSubmit } = useForm<PersonalFormValues>({
        resolver: zodResolver(personalFormSchema),
        defaultValues: {
            personalHighlight: profile.personal.personalHighlight ?? "",
            personalMustKnow: profile.personal.personalMustKnow ?? "",
            title: profile.personal.title ?? "",
            firstName: profile.personal.firstName,
            lastName: profile.personal.lastName,
            greeting: profile.personal.greeting ?? "",
            dateOfBirth: profile.personal.dateOfBirth
                ? parseISO(profile.personal.dateOfBirth)
                : undefined,
            occupation: profile.personal.occupation ?? "",
            memberships: profile.personal.memberships ?? "",
            interests: profile.personal.interests ?? "",
            loves: profile.personal.loves ?? "",
            hates: profile.personal.hates ?? "",
            topCities: profile.personal.topCities ?? [],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "topCities",
    });

    function onSubmit(values: PersonalFormValues) {
        savePersonal(
            {
                title: emptyToUndefined(values.title),
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                greetingsName: values.greeting.trim(),
                personalHighlight: emptyToUndefined(values.personalHighlight),
                personalMustKnow: emptyToUndefined(values.personalMustKnow),
                dateOfBirth: values.dateOfBirth
                    ? format(values.dateOfBirth, "yyyy-MM-dd")
                    : undefined,
                occupation: emptyToUndefined(values.occupation),
                memberships: emptyToUndefined(values.memberships),
                interests: emptyToUndefined(values.interests),
                loves: emptyToUndefined(values.loves),
                hates: emptyToUndefined(values.hates),
                topCities: values.topCities.filter((city) => city.city.trim()),
            },
            onClose,
        );
    }

    return (
        <FormShell
            title="Edit personal information"
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
        >
            <OwnedField
                id="highlight"
                label="Highlight"
                kind="text"
                control={control}
                name="personalHighlight"
            />
            <OwnedField
                id="must-know"
                label="Must know"
                kind="textarea"
                control={control}
                name="personalMustKnow"
            />
            <Controller
                control={control}
                name="title"
                render={({ field }) => (
                    <TitleField
                        id="name-title"
                        value={field.value}
                        onChange={field.onChange}
                    />
                )}
            />
            <OwnedField
                id="name-first"
                label="First name"
                kind="text"
                control={control}
                name="firstName"
                required
            />
            <OwnedField
                id="name-last"
                label="Last name"
                kind="text"
                control={control}
                name="lastName"
                required
            />
            <OwnedField
                id="greeting"
                label="Greeting"
                kind="text"
                control={control}
                name="greeting"
            />
            <OwnedField
                id="date-of-birth"
                label="Date of birth"
                kind="date"
                control={control}
                name="dateOfBirth"
            />
            <OwnedField
                id="occupation"
                label="Occupation"
                kind="text"
                control={control}
                name="occupation"
            />
            <OwnedField
                id="memberships"
                label="Memberships"
                kind="textarea"
                control={control}
                name="memberships"
            />
            <FormSectionLabel className="mt-1">Interests</FormSectionLabel>
            <OwnedField
                id="interests"
                label="Interests"
                kind="textarea"
                control={control}
                name="interests"
            />
            <OwnedField
                id="loves"
                label="Really loves"
                kind="textarea"
                control={control}
                name="loves"
            />
            <OwnedField
                id="hates"
                label="Really hates"
                kind="textarea"
                control={control}
                name="hates"
            />
            <FormSectionLabel className="mt-1">Top locations</FormSectionLabel>
            {fields.map((location, index) => (
                <FormField
                    key={location.id}
                    label={`Location ${index + 1}`}
                    htmlFor={location.id}
                >
                    <div className="flex items-center gap-1">
                        <div className="min-w-0 flex-1">
                            <Controller
                                control={control}
                                name={`topCities.${index}`}
                                render={({ field }) => (
                                    <LocationCombobox
                                        id={location.id}
                                        value={field.value?.city}
                                        placeholder="Search"
                                        onPlaceChange={field.onChange}
                                    />
                                )}
                            />
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Remove location ${index + 1}`}
                            onClick={() => remove(index)}
                        >
                            <X />
                        </Button>
                    </div>
                </FormField>
            ))}
            <Button
                type="button"
                variant="outline"
                size="xs"
                className="self-start"
                onClick={() => append({ city: "", country: "", countryCode: "" })}
            >
                <Plus />
                Add location
            </Button>
            <FormFieldHint>
                These settings affect recommendations the member sees in the app.
            </FormFieldHint>
        </FormShell>
    );
}
